import { Button } from '@/components/ui/button';
import { OrderStatusEnum } from '@libs/schemas/src/order';
import { FC, useState } from 'react';
import { LuTruck } from 'react-icons/lu';
import { ConfirmDialogButton } from 'src/components/ConfirmDialog';
import { Loader } from 'src/components/Loader';
import { useToast } from 'src/components/useToast';
import { Order, trpc } from 'src/trpc';
import { formatTime } from 'src/util';
import { formatCalendarDate, formatDateTime } from '@libs/frontend-services';

interface Props {
  order: Order;
}

interface Info {
  label: string;
  openButtonText: string;
  bodyText: string;
  headerText: string;
  action: string;
  transitionStatus: OrderStatusEnum;
}

export const UpdateOrderStatus: FC<Props> = ({ order }) => {
  const utils = trpc.useUtils();
  const { errorToast } = useToast();
  const { mutate, isPending } = trpc.admin.order.adminSetOrderStatus.useMutation({
    onSuccess: () => {
      utils.admin.order.adminGetOrderRelatedInfoById.invalidate(order._id);
    },
    onError: (error) => {
      errorToast({ description: error.message });
    },
  });

  const [loadingAction, setLoadingAction] = useState<'reschedule' | 'status' | null>(null);

  const handleStatusUpdate = (newStatus: OrderStatusEnum) => {
    setLoadingAction('status');
    mutate(
      {
        status: newStatus,
        orderId: order._id!,
      },
      {
        onSettled: () => setLoadingAction(null),
      },
    );
  };

  const handleReschedule = () => {
    setLoadingAction('reschedule');
    mutate(
      {
        status:
          info.action === 'pickup' ? OrderStatusEnum.PendingPickup : OrderStatusEnum.PendingDropOff,
        orderId: order._id!,
      },
      {
        onSettled: () => setLoadingAction(null),
      },
    );
  };

  const statusInfo: { [key: string]: Info } = {
    [OrderStatusEnum.DropOffScheduled]: {
      label: 'Drop-off Scheduled',
      openButtonText: 'Mark Out for Delivery',
      bodyText: 'Once you confirm, the customer will be notified.',
      headerText: 'Mark Order Out for Delivery',
      action: 'drop-off',
      transitionStatus: OrderStatusEnum.InTransitDropOff,
    },
    [OrderStatusEnum.InTransitDropOff]: {
      label: 'Out for Delivery',
      openButtonText: 'Confirm Delivery',
      bodyText: 'Once you confirm, the customer will be notified.',
      headerText: 'Confirm Delivery',
      action: 'drop-off',
      transitionStatus: OrderStatusEnum.DropOffConfirmed,
    },
    [OrderStatusEnum.PickupScheduled]: {
      label: 'Pick-up Scheduled',
      openButtonText: 'Mark Out for Pick-up',
      bodyText: 'Once you confirm, the customer will be notified.',
      headerText: 'Mark Order Out for Pick-up',
      action: 'pickup',
      transitionStatus: OrderStatusEnum.InTransitPickup,
    },
    [OrderStatusEnum.InTransitPickup]: {
      label: 'Out for Pick-up',
      openButtonText: 'Confirm Pick-up',
      bodyText: 'Once you confirm, the customer will be notified.',
      headerText: 'Confirm Pick-up',
      action: 'pickup',
      transitionStatus: OrderStatusEnum.PickupConfirmed,
    },
    [OrderStatusEnum.PickupConfirmed]: {
      label: 'Dumpster Picked Up',
      openButtonText: 'Confirm Waste Processing',
      bodyText: 'Once you confirm, the customer will be notified.',
      headerText: 'Confirm Waste Processing',
      action: 'pickup',
      transitionStatus: OrderStatusEnum.WasteProcessing,
    },
  };

  const info = statusInfo[order.status];
  if (!info) {
    return null;
  }

  // An order can reach one of these statuses without a container carrying the matching
  // action window (e.g. a status corrected directly in the DB, or a container removed
  // after scheduling). Treat the schedule as unknown rather than assuming it exists —
  // the status transition itself must stay available to admins either way.
  const window = order.containers?.find((c) => c.action === info.action)?.window;
  const scheduledDate = formatCalendarDate(window?.day, 'EEE MMM do yyyy', '');

  return (
    <div className="card flex flex-col justify-start text-left gap-4">
      <p className="text-text-light w-full">Update Order Status</p>
      <div className="rounded-lg border-disabled border-2  px-4 py-3 flex flex-row justify-start gap-4">
        <LuTruck className="w-6 h-6 flex-shrink-0" />
        <div className="min-w-0">
          <p>{info.label}</p>
          <div className="flex flex-col gap-6">
            <p>
              {order.status === OrderStatusEnum.PickupConfirmed
                ? 'Dumpster was picked up'
                : `This order has been scheduled for ${info.action}`}
              {scheduledDate ? (
                <>
                  {' '}
                  on{' '}
                  <b>
                    {scheduledDate}
                    {window?.start !== undefined && window?.end !== undefined && (
                      <> {formatTime(window.start, window.end)}</>
                    )}
                    .
                  </b>
                </>
              ) : (
                <>
                  , but <b>no {info.action} date is recorded</b> for this order.
                </>
              )}
            </p>
            {order.status === OrderStatusEnum.InTransitDropOff && (
              <p>
                It was marked Out for Delivery on{' '}
                <b>
                  {formatDateTime(order.updatedAt, "MMMM d, yyyy 'at' h:mm a")}
                </b>
              </p>
            )}
          </div>
        </div>
      </div>
      {/* Two half-width buttons cannot hold labels like "Change Delivery Date/Time" at the
          mobile type scale, so below `sm` they go full width, one per line. */}
      <div className="flex flex-col sm:flex-row gap-2 w-full">
        {order.status !== OrderStatusEnum.PickupConfirmed && (
          <Button
            disabled={isPending}
            variant={'outline'}
            className="w-full"
            onClick={handleReschedule}
          >
            {loadingAction === 'reschedule' ? (
              <Loader h={4} w={4} />
            ) : (
              `Change ${info.action === 'drop-off' ? 'Delivery' : 'Pick-up'} Date/Time`
            )}
          </Button>
        )}
        <ConfirmDialogButton
          isLoading={loadingAction === 'status'}
          bodyText={info.bodyText}
          headerText={info.headerText}
          onConfirm={() => handleStatusUpdate(info.transitionStatus)}
          openButtonText={info.openButtonText}
          disabled={isPending}
        />
      </div>
    </div>
  );
};
